// Time Tracking modals module for Docospot
// Handles time tracking event create/edit modals and edit history

import { t } from '../core/i18n.js';
import { auth } from '../core/auth.js';
import { apiCall } from '../core/api.js';
import { API_BASE_URL } from '../core/config.js';
import { showError, showSuccess, showConfirmDialog } from '../core/ui.js';
import { loadTimeTrackingData, timeTrackingState } from './time-tracking.js';
import { formatDateTime } from '../utils/helpers.js';

/**
 * Open add/edit manual event modal
 * @param {Object} eventData - Optional event data for edit mode
 */
export async function openAddEventModal(eventData = null) {
    console.log('openAddEventModal called', eventData ? 'in edit mode' : 'in create mode');
    const modal = document.getElementById('modal');
    if (!modal) {
        console.error('Modal element not found');
        return;
    }

    const isEditMode = !!eventData;
    const modalTitle = isEditMode ? t('time.editEvent') : t('time.addManualEvent');
    const saveButtonText = isEditMode ? t('common.update') : t('common.save');

    // Pre-fill values for edit mode or defaults for create mode
    const now = new Date();
    const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    
    // For datetime-local inputs, we need the time in local timezone format
    // The API gives us UTC times with 'Z' suffix, browser Date handles conversion
    let checkInValue = localNow;
    if (eventData?.check_in_time) {
        // Parse UTC time and format for datetime-local (which expects local time string)
        const utcDate = new Date(eventData.check_in_time);
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, '0');
        const day = String(utcDate.getDate()).padStart(2, '0');
        const hours = String(utcDate.getHours()).padStart(2, '0');
        const minutes = String(utcDate.getMinutes()).padStart(2, '0');
        checkInValue = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    
    let checkOutValue = '';
    if (eventData?.check_out_time) {
        const utcDate = new Date(eventData.check_out_time);
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, '0');
        const day = String(utcDate.getDate()).padStart(2, '0');
        const hours = String(utcDate.getHours()).padStart(2, '0');
        const minutes = String(utcDate.getMinutes()).padStart(2, '0');
        checkOutValue = `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    modal.innerHTML = `
        <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-medium text-gray-900">${modalTitle}</h3>
                        <button onclick="closeModal()" class="text-gray-400 hover:text-gray-600">
                            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    
                    <form id="add-event-form" class="space-y-4">
                        <!-- User Selection -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ${t('time.selectUser')} <span class="text-red-600">*</span>
                            </label>
                            <input type="text" id="event-user-search" 
                                   value="${eventData?.user_name || ''}"
                                   placeholder="${t('time.userPlaceholder')}"
                                   class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                            <input type="hidden" id="event-user-id" value="${eventData?.user_id || ''}">
                            <div id="user-search-results" class="mt-1 max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md hidden"></div>
                        </div>

                        <!-- Location Selection -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ${t('time.selectLocation')} <span class="text-red-600">*</span>
                            </label>
                            <input type="text" id="event-location-search" 
                                   value="${eventData?.location_name || ''}"
                                   placeholder="${t('time.locationPlaceholder')}"
                                   class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                            <input type="hidden" id="event-location-id" value="${eventData?.location_id || ''}">
                            <div id="location-search-results" class="mt-1 max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md hidden"></div>
                        </div>

                        <!-- Check-In Time -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ${t('time.checkInTime')} <span class="text-red-600">*</span>
                            </label>
                            <input type="datetime-local" id="event-check-in" value="${checkInValue}" required
                                   class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        </div>

                        <!-- Check-Out Time (Optional) -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ${t('time.checkOutTime')} (${t('common.optional')})
                            </label>
                            <input type="datetime-local" id="event-check-out" value="${checkOutValue}"
                                   class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500">
                        </div>

                        <!-- Reason -->
                        <div>
                            <label class="block text-sm font-medium text-gray-700 mb-2">
                                ${t('time.reason')} (${t('common.optional')})
                            </label>
                            <textarea id="event-reason" rows="3"
                                      class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                                      placeholder="${t('time.reasonPlaceholder')}"></textarea>
                        </div>
                        
                        <!-- Hidden field to track if this is edit mode -->
                        <input type="hidden" id="event-id" value="${eventData?._id || ''}">

                        <!-- Buttons -->
                        <div class="flex space-x-3 pt-4">
                            <button type="submit"
                                    class="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                                ${saveButtonText}
                            </button>
                            <button type="button" onclick="closeModal()"
                                    class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300">
                                ${t('common.cancel')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    `;

    modal.classList.remove('hidden');
    
    // Store original event data for comparison if in edit mode
    if (eventData) {
        modal.dataset.originalEvent = JSON.stringify({
            user_id: eventData.user_id,
            location_id: eventData.location_id,
            check_in_time: eventData.check_in_time,
            check_out_time: eventData.check_out_time
        });
    } else {
        delete modal.dataset.originalEvent;
    }

    // Add form submit handler
    const form = document.getElementById('add-event-form');
    form.addEventListener('submit', handleAddEventSubmit);
    
    // Add search handlers
    setupUserSearch();
    setupLocationSearch();
    
    // Auto-select text in pre-filled fields for easy editing
    if (isEditMode) {
        setTimeout(() => {
            const userSearchInput = document.getElementById('event-user-search');
            if (userSearchInput && userSearchInput.value) {
                userSearchInput.select();
                userSearchInput.focus();
            }
        }, 100);
    }
}

/**
 * Setup user search with debouncing
 */
function setupUserSearch() {
    const searchInput = document.getElementById('event-user-search');
    const resultsDiv = document.getElementById('user-search-results');
    const hiddenInput = document.getElementById('event-user-id');
    let searchTimeout;
    
    searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(searchTimeout);
        
        if (query.length < 2) {
            resultsDiv.classList.add('hidden');
            return;
        }
        
        searchTimeout = setTimeout(async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/users/?search=${encodeURIComponent(query)}&limit=20&include_parent_accounts=true`, {
                    headers: { 'Authorization': `Bearer ${auth.getToken()}` }
                });
                
                if (!response.ok) throw new Error('Search failed');
                
                const data = await response.json();
                const users = data.users || [];
                
                if (users.length === 0) {
                    resultsDiv.innerHTML = '<div class="p-2 text-gray-500">No users found</div>';
                } else {
                    resultsDiv.innerHTML = users.map(user => `
                        <div class="p-2 hover:bg-gray-100 cursor-pointer" onclick="selectUser('${user.id || user._id}', '${(user.full_name || user.username).replace(/'/g, "\\'")}')">
                            ${user.full_name || user.username}${user.email ? ` (${user.email})` : ''}
                        </div>
                    `).join('');
                }
                resultsDiv.classList.remove('hidden');
            } catch (error) {
                console.error('User search error:', error);
            }
        }, 300);
    });
}

/**
 * Setup location search with debouncing
 */
function setupLocationSearch() {
    const searchInput = document.getElementById('event-location-search');
    const resultsDiv = document.getElementById('location-search-results');
    const hiddenInput = document.getElementById('event-location-id');
    let searchTimeout;
    
    searchInput.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(searchTimeout);
        
        if (query.length < 2) {
            resultsDiv.classList.add('hidden');
            return;
        }
        
        searchTimeout = setTimeout(async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/locations/?search=${encodeURIComponent(query)}&limit=20`, {
                    headers: { 'Authorization': `Bearer ${auth.getToken()}` }
                });
                
                if (!response.ok) throw new Error('Search failed');
                
                const data = await response.json();
                const locations = data.locations || [];
                
                if (locations.length === 0) {
                    resultsDiv.innerHTML = '<div class="p-2 text-gray-500">No locations found</div>';
                } else {
                    resultsDiv.innerHTML = locations.map(location => `
                        <div class="p-2 hover:bg-gray-100 cursor-pointer" onclick="selectLocation('${location.id || location._id}', '${(location.name).replace(/'/g, "\\'")}')">
                            ${location.name}${location.address ? ` - ${location.address}` : ''}
                        </div>
                    `).join('');
                }
                resultsDiv.classList.remove('hidden');
            } catch (error) {
                console.error('Location search error:', error);
            }
        }, 300);
    });
}

/**
 * Select a user from search results
 */
function selectUser(userId, userName) {
    document.getElementById('event-user-id').value = userId;
    document.getElementById('event-user-search').value = userName;
    document.getElementById('user-search-results').classList.add('hidden');
}

/**
 * Select a location from search results
 */
function selectLocation(locationId, locationName) {
    document.getElementById('event-location-id').value = locationId;
    document.getElementById('event-location-search').value = locationName;
    document.getElementById('location-search-results').classList.add('hidden');
}

/**
 * Handle add/edit event form submission
 */
async function handleAddEventSubmit(event) {
    event.preventDefault();
    
    const eventId = document.getElementById('event-id').value;
    const isEditMode = !!eventId;
    
    const userId = document.getElementById('event-user-id').value;
    const locationId = document.getElementById('event-location-id').value;
    const checkInTime = document.getElementById('event-check-in').value;
    const checkOutTime = document.getElementById('event-check-out').value;
    const reason = document.getElementById('event-reason').value;
    
    // Validation
    if (!userId) {
        showError(t('time.selectUserRequired'));
        return;
    }
    
    if (!locationId) {
        showError(t('time.selectLocationRequired'));
        return;
    }
    
    if (!checkInTime) {
        showError(t('time.checkInRequired'));
        return;
    }
    
    try {
        if (isEditMode) {
            // Update existing event - only send fields that actually changed
            const originalEvent = JSON.parse(modal.dataset.originalEvent || '{}');
            const requestBody = {};
            
            // Only send user_id if it changed
            if (userId !== originalEvent.user_id) {
                requestBody.user_id = userId;
            }
            
            // Only send location_id if it changed
            if (locationId !== originalEvent.location_id) {
                requestBody.location_id = locationId;
            }
            
            // Only send check_in_time if it actually changed (compare to minute precision)
            const newCheckInISO = new Date(checkInTime).toISOString();
            const oldCheckInISO = originalEvent.check_in_time;
            // Compare times ignoring seconds and milliseconds (datetime-local only has minute precision)
            const newCheckInMinutes = newCheckInISO.substring(0, 16); // YYYY-MM-DDTHH:MM
            const oldCheckInMinutes = (oldCheckInISO || '').substring(0, 16);
            if (newCheckInMinutes !== oldCheckInMinutes) {
                requestBody.check_in_time = newCheckInISO;
            }
            
            // Only send check_out_time if it actually changed
            if (checkOutTime) {
                const newCheckOutISO = new Date(checkOutTime).toISOString();
                const oldCheckOutISO = originalEvent.check_out_time;
                const newCheckOutMinutes = newCheckOutISO.substring(0, 16);
                const oldCheckOutMinutes = (oldCheckOutISO || '').substring(0, 16);
                if (newCheckOutMinutes !== oldCheckOutMinutes) {
                    requestBody.check_out_time = newCheckOutISO;
                }
            } else if (originalEvent.check_out_time) {
                // Check-out was cleared
                requestBody.check_out_time = null;
            }
            
            // Only proceed if there are actual changes
            if (Object.keys(requestBody).length === 0) {
                closeModal();
                showSuccess(t('time.noChanges'));
                return;
            }
            
            // Add reason if provided or generate default
            requestBody.reason = reason || 'Event updated';
            
            const response = await fetch(`${API_BASE_URL}/time-tracking/events/${eventId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.getToken()}`
                },
                body: JSON.stringify(requestBody)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || t('time.eventUpdateError'));
            }
            
            closeModal();
            showSuccess(t('time.eventUpdatedSuccess'));
            
        } else {
            // Create new event
            const requestBody = {
                user_id: userId,
                location_id: locationId,
                check_in_time: new Date(checkInTime).toISOString(),
                check_out_time: checkOutTime ? new Date(checkOutTime).toISOString() : null,
                reason: reason || 'Manual entry'
            };
            
            const response = await fetch(`${API_BASE_URL}/time-tracking/events/manual`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth.getToken()}`
                },
                body: JSON.stringify(requestBody)
            });
            
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || t('time.eventCreateError'));
            }
            
            closeModal();
            showSuccess(t('time.eventCreatedSuccess'));
        }
        
        // Reload time tracking data
        await loadTimeTrackingData(true);
        
    } catch (error) {
        console.error('Error saving event:', error);
        showError(error.message);
    }
}

/**
 * Open edit time modal for individual field editing
 */
export function openEditTimeModal(element) {
    const eventId = element.getAttribute('data-event-id');
    const focusField = element.getAttribute('data-field'); // which field was clicked
    
    // Find the event in state
    const event = timeTrackingState.allEvents.find(e => e._id === eventId);
    if (!event) {
        showError('Begivenhed ikke fundet');
        return;
    }
    
    const toLocalInputStr = (isoStr) => {
        if (!isoStr) return '';
        const d = new Date(isoStr);
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    const checkInRaw = toLocalInputStr(event.check_in_time);
    const checkOutRaw = toLocalInputStr(event.check_out_time);
    // Pre-fill the missing time with the other as a starting point (saves clicks)
    const checkInDateTime = checkInRaw || checkOutRaw;
    const checkOutDateTime = checkOutRaw || checkInRaw;
    
    // Create modal
    const modal = document.createElement('div');
    modal.id = 'edit-time-modal';
    modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
    modal.innerHTML = `
        <div class="relative top-20 p-5 border w-96 shadow-lg rounded-md bg-white" style="margin-left: auto; margin-right: 15%;">
            <div class="mt-3">
                <h3 class="text-lg font-medium text-gray-900 mb-4">${t('time.edit')} ${t('time.times') || 'Times'}</h3>
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        ${t('time.userName')}: <span class="font-normal">${event.user_name || 'Unknown'}</span>
                    </label>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                        ${t('time.location')}: <span class="font-normal">${event.location_name || 'Unknown'}</span>
                    </label>
                </div>
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.checkInTime') || 'Check-in Time'}</label>
                    <input type="datetime-local" id="edit-checkin-input" value="${checkInDateTime}"
                           class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                </div>
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.checkOutTime') || 'Check-out Time'}</label>
                    <input type="datetime-local" id="edit-checkout-input" value="${checkOutDateTime}"
                           class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm">
                </div>
                <div class="mb-4">
                    <label class="block text-sm font-medium text-gray-700 mb-1">${t('time.reason')} (${t('common.optional')})</label>
                    <textarea id="edit-time-reason" rows="2"
                              class="block w-full px-3 py-2 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              placeholder="${t('time.reasonPlaceholder')}"></textarea>
                </div>
                <div class="flex space-x-3">
                    <button onclick="saveTimeEdit('${eventId}')"
                            class="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700">
                        ${t('common.save')}
                    </button>
                    <button onclick="closeEditTimeModal()"
                            class="flex-1 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300">
                        ${t('common.cancel')}
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(modal);

    // Focus the field that was clicked
    const focusId = focusField === 'check_in_time' ? 'edit-checkin-input' : 'edit-checkout-input';
    setTimeout(() => {
        const input = document.getElementById(focusId);
        if (input) input.focus();
    }, 50);
}

/**
 * Close edit time modal
 */
function closeEditTimeModal() {
    const modal = document.getElementById('edit-time-modal');
    if (modal) {
        modal.remove();
    }
}

/**
 * Save time edit
 */
async function saveTimeEdit(eventId) {
    const newCheckIn = document.getElementById('edit-checkin-input').value;
    const newCheckOut = document.getElementById('edit-checkout-input').value;
    const reason = document.getElementById('edit-time-reason').value;
    
    if (!newCheckIn && !newCheckOut) {
        showError(t('time.enterAtLeastOneTime'));
        return;
    }
    
    try {
        const body = { reason };
        if (newCheckIn) body.check_in_time = new Date(newCheckIn).toISOString();
        if (newCheckOut) body.check_out_time = new Date(newCheckOut).toISOString();
        
        const response = await fetch(`${API_BASE_URL}/time-tracking/events/${eventId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${auth.getToken()}`
            },
            body: JSON.stringify(body)
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to update event');
        }
        
        closeEditTimeModal();
        showSuccess(t('time.eventUpdatedSuccess'));

        // Set pending highlight so it survives any WS-triggered reloads
        if (timeTrackingState.pendingHighlightTimer) {
            clearTimeout(timeTrackingState.pendingHighlightTimer);
        }
        timeTrackingState.pendingHighlightEventId = eventId;
        timeTrackingState.pendingHighlightTimer = setTimeout(() => {
            const rows = document.querySelectorAll('#time-tracking-list tr');
            rows.forEach(row => {
                const spans = row.querySelectorAll('span[data-event-id]');
                spans.forEach(span => {
                    if (span.getAttribute('data-event-id') === eventId) {
                        row.classList.add('transition-colors', 'duration-1000');
                        row.classList.remove('bg-yellow-100');
                    }
                });
            });
            timeTrackingState.pendingHighlightEventId = null;
            timeTrackingState.pendingHighlightTimer = null;
        }, 1000);

        await loadTimeTrackingData(true);  // Reload data with reset=true to force refresh
        
    } catch (error) {
        console.error('Error updating time:', error);
        showError(error.message);
    }
}

/**
 * View edit history for an event
 */
async function viewEditHistory(eventId) {
    try {
        const response = await fetch(`${API_BASE_URL}/time-tracking/events/${eventId}/history`, {
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        
        if (!response.ok) {
            throw new Error('Failed to fetch edit history');
        }
        
        const data = await response.json();
        
        // Create history modal
        const modal = document.createElement('div');
        modal.id = 'edit-history-modal';
        modal.className = 'fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50';
        
        const historyRows = data.history.map(entry => {
            const isManualCreation = entry.field_changed === 'manual_creation';
            const isImported = entry.field_changed === 'bulk_import' || entry.change_type === 'Imported';
            
            let changeType;
            if (isImported) {
                changeType = 'Imported';
            } else if (isManualCreation) {
                changeType = t('time.manualCreation');
            } else if (entry.field_changed === 'check_in_time') {
                changeType = t('time.checkInEdit');
            } else {
                changeType = t('time.checkOutEdit');
            }
            
            const oldCheckIn = (isManualCreation || isImported) ? '-' : (entry.field_changed === 'check_in_time' && entry.old_value ? formatDateTime(entry.old_value) : '-');
            const oldCheckOut = (isManualCreation || isImported) ? '-' : (entry.field_changed === 'check_out_time' && entry.old_value ? formatDateTime(entry.old_value) : '-');
            const newCheckIn = (entry.field_changed === 'check_in_time' && entry.new_value) ? formatDateTime(entry.new_value) : '-';
            const newCheckOut = (entry.field_changed === 'check_out_time' && entry.new_value) ? formatDateTime(entry.new_value) : '-';
            
            return `
            <tr class="border-b">
                <td class="px-4 py-3 text-sm">${formatDateTime(entry.edited_at)}</td>
                <td class="px-4 py-3 text-sm">${entry.edited_by_user_name}</td>
                <td class="px-4 py-3 text-sm font-medium">${changeType}</td>
                <td class="px-4 py-3 text-sm">${oldCheckIn}</td>
                <td class="px-4 py-3 text-sm">${oldCheckOut}</td>
                <td class="px-4 py-3 text-sm">${newCheckIn}</td>
                <td class="px-4 py-3 text-sm">${newCheckOut}</td>
                <td class="px-4 py-3 text-sm text-gray-600">${entry.reason || '-'}</td>
            </tr>
            `;
        }).join('');
        
        modal.innerHTML = `
            <div class="relative top-20 ml-8 mr-auto p-5 border w-3/4 max-w-6xl shadow-lg rounded-md bg-white">
                <div class="mt-3">
                    <div class="flex justify-between items-center mb-4">
                        <h3 class="text-lg font-medium text-gray-900">${t('time.changeLog')}</h3>
                        <button onclick="closeEditHistoryModal()" class="text-gray-400 hover:text-gray-600">
                            <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    <div class="text-sm text-gray-500 mb-4">
                        ${t('time.totalChanges')}: ${data.total_edits}
                    </div>
                    ${data.history.length > 0 ? `
                        <div class="overflow-x-auto">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.editedAt')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.editedBy')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.change')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.oldCheckIn')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.oldCheckOut')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.newCheckIn')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.newCheckOut')}</th>
                                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">${t('time.reason')}</th>
                                    </tr>
                                </thead>
                                <tbody class="bg-white divide-y divide-gray-200">
                                    ${historyRows}
                                </tbody>
                            </table>
                        </div>
                    ` : `
                        <div class="text-center py-8 text-gray-500">
                            ${t('time.noEditHistory')}
                        </div>
                    `}
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
    } catch (error) {
        console.error('Error fetching edit history:', error);
        showError('Failed to load edit history');
    }
}

/**
 * Close edit history modal
 */
function closeEditHistoryModal() {
    const modal = document.getElementById('edit-history-modal');
    if (modal) {
        modal.remove();
    }
}

/**
 * Close modal
 */
function closeModal() {
    const modal = document.getElementById('modal');
    if (modal) {
        modal.classList.add('hidden');
        modal.innerHTML = '';
    }
}

/**
 * Delete a time tracking event
 */
async function deleteTimeTrackingEvent(eventId) {
    const confirmed = await showConfirmDialog({
        title: t('time.deleteEvent'),
        message: t('time.deleteConfirm'),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel'),
        confirmClass: 'bg-red-600 hover:bg-red-500',
        icon: 'danger'
    });
    
    if (!confirmed) {
        return;
    }
    
    try {
        const response = await fetch(`${API_BASE_URL}/time-tracking/events/${eventId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to delete event');
        }
        
        showSuccess(t('common.deleteSuccess'));
        
        // Check which tab is currently active and refresh appropriately
        const timeBudgetTab = document.getElementById('time-budget-tab');
        const isOnBudgetTab = timeBudgetTab && timeBudgetTab.className.includes('text-primary-600');
        
        if (isOnBudgetTab) {
            // Find which location was expanded before deleting
            let expandedLocationId = null;
            const detailRows = document.querySelectorAll('[id^="budget-detail-"]');
            detailRows.forEach(row => {
                if (!row.classList.contains('hidden')) {
                    // Extract location ID from element ID (format: budget-detail-{locationId})
                    expandedLocationId = row.id.replace('budget-detail-', '');
                }
            });
            
            // Refresh the budget view by reloading data
            if (window.loadTimeBudgetData) {
                await window.loadTimeBudgetData(true);
            } else if (window.filterTimeBudget) {
                await window.filterTimeBudget();
            }
            
            // Re-expand the location after refresh
            if (expandedLocationId && window.toggleBudgetLocationExpand) {
                // Wait a bit for the DOM to be ready
                setTimeout(() => {
                    window.toggleBudgetLocationExpand(expandedLocationId);
                }, 100);
            }
        } else {
            // Refresh the regular time tracking list
            await loadTimeTrackingData(true);
        }
        
    } catch (error) {
        console.error('Error deleting event:', error);
        showError(error.message);
    }
}

/**
 * Copy a time tracking event
 */
async function copyTimeTrackingEvent(eventId) {
    try {
        const response = await fetch(`${API_BASE_URL}/time-tracking/events/${eventId}/copy`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${auth.getToken()}`,
                'Content-Type': 'application/json'
            }
        });
        
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to copy event');
        }
        
        const result = await response.json();
        showSuccess(t('time.copySuccess'));
        
        // Refresh the list first
        await loadTimeTrackingData(true);
        
        // Open edit modal with the newly created event
        const newEvent = result.new_event;
        if (newEvent) {
            // Give a brief moment for the UI to update
            setTimeout(() => {
                openAddEventModal(newEvent);
            }, 300);
        }
        
    } catch (error) {
        console.error('Error copying event:', error);
        showError(error.message);
    }
}

/**
 * Edit a time tracking event (opens edit modal)
 */
async function editTimeTrackingEvent(eventId) {
    // Find the event in state
    const event = timeTrackingState.allEvents.find(e => e._id === eventId);
    if (!event) {
        showError('Begivenhed ikke fundet');
        return;
    }
    
    // Open the add/edit modal in edit mode
    openAddEventModal(event);
}

// Export functions to window for onclick handlers
window.openAddEventModal = openAddEventModal;
window.openEditTimeModal = openEditTimeModal;
window.closeEditTimeModal = closeEditTimeModal;
window.saveTimeEdit = saveTimeEdit;
window.viewEditHistory = viewEditHistory;
window.closeEditHistoryModal = closeEditHistoryModal;
window.closeModal = closeModal;
window.deleteTimeTrackingEvent = deleteTimeTrackingEvent;
window.copyTimeTrackingEvent = copyTimeTrackingEvent;
window.editTimeTrackingEvent = editTimeTrackingEvent;
window.selectUser = selectUser;
window.selectLocation = selectLocation;

export default {
    openAddEventModal,
    openEditTimeModal,
    deleteTimeTrackingEvent,
    copyTimeTrackingEvent,
    editTimeTrackingEvent
};
